import { Injectable } from '@angular/core';
import { NumberUtils } from '@modeso/aumico-lib-core-fe';
import { IFinancialLineItemComponent, IFli, IManualAccount } from '@modeso/types__aumico-lib-annualreport-be';
import { IDynamicKeyValue } from '@modeso/types__aumico-lib-core-be';
import { IFinancialLineItemOptions } from '@modeso/types__aumico-lib-notes-be';
import { cloneDeep } from 'lodash-es';
import { ITableWithForm } from '../components/tableWithForm/ITableWithForm.interface';
import { IFliTotalRow } from '../interfaces/IFliTotalRow.interface';
@Injectable({
    providedIn: 'root',
})
export class FliHelper {
    getFliComponentMockedData(): IFinancialLineItemComponent {
        const fliData: IFinancialLineItemComponent = {
            options: { text: {} as IDynamicKeyValue } as IFinancialLineItemOptions,
            currentBalanceYearDate: '2021-12-31T22:00:00.000Z',
            previousBalanceYearDate: '2020-12-31T22:00:00.000Z',
            currentIncomeYearDate: '2021-12-29T22:00:00.000Z',
            previousIncomeYearDate: '2020-12-28T22:00:00.000Z',
            flis: [] as IFli[],
        };
        return fliData;
    }

    updateWithFormChange(
        id: string,
        fliData: IFinancialLineItemComponent,
        change: ITableWithForm,
    ): IFinancialLineItemComponent {
        const updatedFliData: IFinancialLineItemComponent = cloneDeep(fliData);
        updatedFliData.flis.forEach((fli) => {
            if (fli.id == id) {
                fli.accounts = change.formValue as IManualAccount[];
                fli.totalRow = change.totalRowValue as IFliTotalRow;
            }
        });

        return updatedFliData;
    }

    getDifferenceOfFlisById(firstFlis: IFli[], secondFlis: IFli[]): IFli[] {
        const difference: IFli[] = [];

        for (const firstFli of firstFlis) {
            const isFliExist = secondFlis.find((secondFli) => secondFli.id == firstFli.id);

            // element of the first flis must be pushed as it has the updated data
            if (isFliExist != null) {
                difference.push(firstFli);
            }
        }
        return difference;
    }

    getFlisFromFormChange(fliFormChanges: [{ fli: IFli }]): IFli[] {
        const changeOfFlis: IFli[] = [];

        for (const fliFormChange of fliFormChanges) {
            if (fliFormChange != null) {
                changeOfFlis.push(fliFormChange.fli);
            }
        }

        return changeOfFlis;
    }

    getUpdatedFlis(currentFlis: IFli[], allFlis: IFli[]): IFli[] {
        const updatedFlis: IFli[] = [];
        for (const currentFli of currentFlis) {
            const oldFli = { ...currentFli };
            const updatedVersionOfCurrentFli: IFli = allFlis.find((fli: IFli) => fli.id === currentFli.id);

            // This check because user can remove sub items from the mapping, so it should not be displayed anymore in notes
            if (updatedVersionOfCurrentFli != null) {
                // get the update of accounts of the fli as difference
                const extraAccountsAdded: IManualAccount[] = this.getExtraAccountsOfUpdate(
                    currentFli.accounts,
                    updatedVersionOfCurrentFli.accounts,
                );
                oldFli.accounts = [...extraAccountsAdded];
                oldFli.caption = updatedVersionOfCurrentFli.caption;
                updatedFlis.push(oldFli);
            }
        }

        return updatedFlis;
    }

    private getExtraAccountsOfUpdate(
        currentAccounts: IManualAccount[],
        updatedAccounts: IManualAccount[],
    ): IManualAccount[] {
        const differenceOFAccounts: IManualAccount[] = [];

        // The two legs of a balance-switch account share the id; a saved row that knows its leg
        // must pair with the live row of the SAME leg, a legacy row without one pairs by id alone.
        const legOf = (row: IManualAccount): boolean | undefined =>
            (row as IManualAccount & { originsFromAsset?: boolean }).originsFromAsset;
        const withLiveLeg = (row: IManualAccount, live: IManualAccount): IManualAccount =>
            typeof legOf(live) === 'boolean' ? ({ ...row, originsFromAsset: legOf(live) } as IManualAccount) : row;

        for (const account of updatedAccounts) {
            const currentAccount: IManualAccount = currentAccounts.find(
                (oldAccount: IManualAccount) =>
                    oldAccount.id === account.id && (legOf(oldAccount) == null || legOf(oldAccount) === legOf(account)),
            );

            // new account added
            if (currentAccount == null) {
                differenceOFAccounts.push(account);
                continue;
            }
            const isAccountValuesNotChanged =
                NumberUtils.areNumbersEqual(currentAccount.currentValue, account.currentValue) &&
                NumberUtils.areNumbersEqual(currentAccount.previousValue, account.previousValue);

            if (!isAccountValuesNotChanged) {
                const accountWithUpdatedValues = withLiveLeg({ ...currentAccount }, account);
                accountWithUpdatedValues.currentValue = NumberUtils.roundNumber(Number(account.currentValue));
                accountWithUpdatedValues.previousValue = NumberUtils.roundNumber(Number(account.previousValue));
                // IMPORTANT: Also update the account name to reflect any renaming from grouping & masking
                accountWithUpdatedValues.accountName = account.accountName;
                differenceOFAccounts.push(accountWithUpdatedValues);
                continue;
            }

            // Even if values haven't changed, update the account name to reflect any renaming
            const accountWithUpdatedName = withLiveLeg({ ...currentAccount }, account);
            accountWithUpdatedName.accountName = account.accountName;
            differenceOFAccounts.push(accountWithUpdatedName);
        }

        return differenceOFAccounts;
    }
}
